import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../../i18n/LanguageContext';
import { FaStar, FaQuoteRight } from 'react-icons/fa';

const REVIEWS = [
  {
    name: 'Emily Daniel',
    roleKey: 'landing.testimonials.r1Role',
    company: 'GrowthLabs Agency',
    rating: 5,
    textKey: 'landing.testimonials.r1Text',
    avatar: 'ED',
    color: '#c7bfff',
    bg: '#F5F7FF',
    border: '#E0E4F0',
  },
  {
    name: 'Marcus Rivera',
    roleKey: 'landing.testimonials.r2Role',
    company: 'Nexus eCommerce',
    rating: 5,
    textKey: 'landing.testimonials.r2Text',
    avatar: 'MR',
    color: '#c7bfff',
    featured: true,
  },
  {
    name: 'Alex Mien',
    roleKey: 'landing.testimonials.r3Role',
    company: 'ScaleUp Media',
    rating: 5,
    textKey: 'landing.testimonials.r3Text',
    avatar: 'AM',
    color: '#059669',
    bg: '#ECFDF5',
    border: '#A7F3D0',
  },
];

function RatingBadge({ platform, score, count, color, bg, border, label }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 18px', borderRadius: 12,
      background: 'linear-gradient(160deg, rgba(24,19,60,.72), rgba(11,8,32,.88))', border: '1px solid rgba(255,255,255,0.09)',
      boxShadow: '0 16px 40px -18px rgba(0,0,0,0.6)',
    }}>
      <div style={{ fontSize: 18, fontWeight: 900, color, lineHeight: 1 }}>{score}</div>
      <div>
        <div style={{ display: 'flex', gap: 2, marginBottom: 2 }}>
          {[1,2,3,4,5].map(i => <FaStar key={i} style={{ fontSize: 9, color: '#F59E0B' }} />)}
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.42)', whiteSpace: 'nowrap' }}>{count} {label} · {platform}</div>
      </div>
    </div>
  );
}

const Testimonials = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="testimonials" ref={ref} style={{ background: '#0A0722', padding: '96px 24px', position: 'relative', overflow: 'hidden' }}>
      <div className="oba-aurora" style={{ bottom: '-16%', left: '-8%', width: 520, height: 520, background: 'radial-gradient(circle, rgba(217,70,239,.16) 0%, transparent 68%)' }} />
      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <span style={{ display: 'inline-block', padding: '5px 16px', borderRadius: 999, background: 'rgba(109,93,251,0.14)', border: '1px solid rgba(109,93,251,0.38)', color: '#c7bfff', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 18 }}>
            {t('landing.testimonials.badge')}
          </span>
          <h2 style={{ fontSize: 'clamp(26px,4vw,42px)', fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', marginBottom: 12, lineHeight: 1.2 }}>
            {t('landing.testimonials.title')}
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.62)', maxWidth: 440, margin: '0 auto 32px', lineHeight: 1.65 }}>
            {t('landing.testimonials.sub')}
          </p>

          {/* Review platform badges */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap',
            opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(12px)', transition: 'all 0.5s ease' }}>
            <RatingBadge platform="G2" score="4.9" count="148" color="#ff492c" bg="#FFF5F5" border="#FED7D7" label={t('landing.testimonials.reviews')} />
            <RatingBadge platform="Capterra" score="4.8" count="96"  color="#0ea5e9" bg="#F0F9FF" border="#BAE6FD" label={t('landing.testimonials.reviews')} />
            <RatingBadge platform="Product Hunt" score="4.9" count="212" color="#da552f" bg="#FFF7F5" border="#FED7AA" label={t('landing.testimonials.reviews')} />
          </div>
        </div>

        {/* Review cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px,1fr))', gap: 20, alignItems: 'start' }}>
          {REVIEWS.map((r, i) => (
            <div key={i} style={{
              padding: '28px 26px',
              borderRadius: 18,
              background: r.featured
                ? 'linear-gradient(160deg, rgba(79,70,229,.32), rgba(17,13,48,.95))'
                : 'linear-gradient(160deg, rgba(24,19,60,.72), rgba(11,8,32,.88))',
              border: r.featured ? '1px solid rgba(139,124,255,.42)' : '1px solid rgba(255,255,255,0.09)',
              boxShadow: r.featured
                ? '0 30px 70px -20px rgba(79,70,229,.45)'
                : '0 24px 60px -22px rgba(0,0,0,0.7)',
              opacity: visible ? 1 : 0,
              transform: visible ? 'none' : 'translateY(28px)',
              transition: `all 0.6s ${i * 120}ms ease`,
              position: 'relative',
            }}>
              {r.featured && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: '#fff',
                  color: '#c7bfff', fontSize: 10, fontWeight: 800,
                  padding: '4px 14px', borderRadius: 999, letterSpacing: '0.08em',
                  whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(34,28,81,0.2)',
                }}>
                  {t('landing.testimonials.featured')}
                </div>
              )}

              <FaQuoteRight style={{ fontSize: 20, color: r.featured ? 'rgba(255,255,255,0.32)' : 'rgba(167,139,250,0.55)', marginBottom: 16 }} />

              <p style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.82)', lineHeight: 1.75, marginBottom: 24 }}>{t(r.textKey)}</p>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: '50%',
                  background: 'rgba(255,255,255,0.09)',
                  border: '1.5px solid rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, color: '#c7bfff', flexShrink: 0,
                }}>
                  {r.avatar}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{t(r.roleKey)} · {r.company}</div>
                </div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {Array.from({ length: r.rating }).map((_, j) => (
                    <FaStar key={j} style={{ color: '#F59E0B', fontSize: 11 }} />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom social proof bar */}
        <div style={{
          marginTop: 40, padding: '18px 28px',
          background: 'linear-gradient(160deg, rgba(24,19,60,.6), rgba(11,8,32,.8))',
          border: '1px solid rgba(255,255,255,0.09)',
          borderRadius: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, flexWrap: 'wrap',
          opacity: visible ? 1 : 0, transition: 'opacity 0.8s 0.4s ease',
        }}>
          <div style={{ display: 'flex', marginRight: 4 }}>
            {['#221C51','#1877f2','#059669','#D97706','#7C3AED'].map((c, i) => (
              <div key={i} style={{ width: 28, height: 28, borderRadius: '50%', background: `${c}18`, border: `2px solid ${c}40`, marginLeft: i === 0 ? 0 : -8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: c }}>
                {['ED','MR','AM','JK','SL'][i]}
              </div>
            ))}
          </div>
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.62)' }}>
            {t('landing.testimonials.joinText')}
          </span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.42)', marginLeft: 4 }}>· {t('landing.testimonials.avgRating')}</span>
        </div>

      </div>
    </section>
  );
};

export default Testimonials;
